// Help Assistant — data access. Reads go through definer RPCs (help_library, help_my_requests, …); new requests go
// through the `help` edge function (it masks, stores, groups duplicates and emails urgent ones).
// Every call destructures {data, error} (CLAUDE.md GR#2) and logs failures with ❌.

const H = () => window.HUD;
const supa = () => window.HUD?._supa;

let _lib = null;          // library rows (cached for the session)
let _libAt = 0;

let _missing = false;
export const libraryMissing = () => _missing;
let _failed = false;   // the last library request failed (network or server error): NOT the same as an empty or missing library
export const libraryFailed = () => _failed;
export async function loadLibrary(force = false) {
  if (_lib && !force && Date.now() - _libAt < 30 * 60 * 1000) return _lib;
  try {
    _failed = false;
    const { data, error } = await supa().rpc('help_library');
    if (error) {
      // Not installed (the H1 migration isn't applied): stay quiet and tell boot() to stop trying.
      if (error.code === 'PGRST202' || error.code === '42883' || /could not find the function/i.test(error.message || '')) { _missing = true; return []; }
      _failed = true; console.error('❌ help_library:', error); return _lib || [];
    }
    _lib = Array.isArray(data) ? data : [];
    _libAt = Date.now();
  } catch (e) { _failed = true; console.error('❌ help_library:', e); }
  return _lib || [];
}

export async function myRequests() {
  try {
    const { data, error } = await supa().rpc('help_my_requests');
    if (error) { console.error('❌ help_my_requests:', error); return null; }
    return Array.isArray(data) ? data : [];
  } catch (e) { console.error('❌ help_my_requests:', e); return null; }
}

export function markSeen(id) {
  // fire-and-forget, but dispatched (a bare builder never sends — CLAUDE.md gotcha)
  supa().rpc('help_mark_seen', { p_id: id })
    .then(({ error }) => { if (error) console.error('❌ help_mark_seen:', error); }, e => console.error('❌ help_mark_seen:', e));
}

export function feedback(code, helped, query, lang) {
  supa().rpc('help_feedback_submit', { p_code: code, p_helped: helped, p_query: query ? maskText(query) : null, p_lang: lang })
    .then(({ error }) => { if (error) console.error('❌ help_feedback_submit:', error); }, e => console.error('❌ help_feedback_submit:', e));
}

// kind: 'howto' | 'bug' | 'idea' | 'urgent'
export async function submitRequest({ kind, text, ctx, extra }) {
  const body = {
    action: 'submit', kind, text: maskText(text), role: ctx.role, lang: ctx.lang, screen: ctx.screen,
    device: ctx.device, app_build: ctx.build, extra: extra || {},
  };
  try {
    const { data, error } = await supa().functions.invoke('help', { body });
    if (error) { console.error('❌ help submit:', error); return { ok: false }; }
    return { ok: true, ...(data || {}) };
  } catch (e) { console.error('❌ help submit:', e); return { ok: false }; }
}

export async function adminReport(fromIso, toIso) {
  const { data, error } = await supa().rpc('help_admin_report', { p_from: fromIso, p_to: toIso });
  if (error) { console.error('❌ help_admin_report:', error); throw error; }
  return data;
}

export async function adminVerdict(id, verdict, note) {
  const { error } = await supa().rpc('help_admin_verdict', { p_id: id, p_verdict: verdict, p_note: note || null });
  if (error) { console.error('❌ help_admin_verdict:', error); throw error; }
}

// The request statuses in which an answer has actually been SENT to the user: the agent API sets exactly these when it
// replies (reply_existing / reply_text / publish → answered, open_bug → bug_open, bug_fixed, not_possible →
// idea_logged). Any other status (held, working, new, human) may carry an unapproved draft in the answer columns, so its
// answer text is never shown. The database applies the same list (HELP-h2's help_my_requests); this is the client's copy.
export const SENT_STATUSES = Object.freeze(['answered', 'bug_open', 'bug_fixed', 'idea_logged']);
export const visibleAnswer = r => (r && SENT_STATUSES.includes(r.status) ? r.answer || null : null);
// the same gate for the answer's library code: a held new video (HELP-fn-1 publish) is not offered before it goes live
export const visibleCode = r => (r && SENT_STATUSES.includes(r.status) ? r.question_code || null : null);

export function videoUrl(path) {
  if (!path) return null;
  if (window.__HELP_VIDEO_BASE) return window.__HELP_VIDEO_BASE + path; // the demo sandbox serves its own copies
  try { return supa().storage.from('help-videos').getPublicUrl(path).data.publicUrl; } catch (e) { return null; }
}

// Client-side privacy pass (the server masks again): names we know, emails, long numbers (IDs, phones).
export function maskText(s) {
  let out = String(s || '').slice(0, 2000);
  const names = new Set();
  try {
    const DB = H()?.DB;
    (DB?.children || []).forEach(c => { if (c?.name) names.add(String(c.name).split(' ')[0]); if (c?.nameAlt) names.add(String(c.nameAlt).split(' ')[0]); });
    (DB?.specialistPatients || []).forEach(p => { if (p?.name) names.add(String(p.name).split(' ')[0]); });
  } catch (_) {}
  for (const n of names) {
    if (!n || n.length < 2) continue;
    out = out.replace(new RegExp('(^|[^\\p{L}])' + n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(?=$|[^\\p{L}])', 'gu'), '$1[name]');
  }
  out = out.replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email]').replace(/\+?\d[\d\s-]{6,}\d/g, '[number]');
  return out;
}
